import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Badge,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Button,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  CircularProgress,
  alpha,
  Tooltip
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
  Notifications,
  NotificationsActive,
  CheckCircle,
  Info,
  Schedule,
  Person,
  Close,
  MarkEmailRead,
  MarkEmailUnread,
  Comment,
  Reply,
  ConfirmationNumber,
  PersonAdd,
  Description,
  Update,
  ShoppingCart,
  LocalShipping,
  OpenInNew
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import dayjs from 'dayjs';

// Topbar bell rattle: keyed via the @mui/system keyframes helper so the
// generated animation name + the @keyframes block are guaranteed to match
// (string-named inline @keyframes inside nested sx selectors are
// unreliable under Emotion). Only fired on hover when unreadCount > 0.
const bellRattle = keyframes`
  0%, 100% { transform: rotate(0deg); }
  20% { transform: rotate(-14deg); }
  40% { transform: rotate(12deg); }
  60% { transform: rotate(-10deg); }
  80% { transform: rotate(8deg); }
`;

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  created_at?: string;
  createdAt?: string;
  read: boolean;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  action_url?: string;
  entity_type?: string;
  entity_id?: string;
  metadata?: any;
  triggeredBy?: {
    id: string;
    first_name: string;
    last_name: string;
    profile_picture?: string;
  };
}

interface NotificationCenterProps {
  notifications?: Notification[];
  onNotificationClick?: (notification: Notification) => void;
  onMarkAsRead?: (notificationId: string) => void;
  onMarkAllAsRead?: () => void;
  onDeleteNotification?: (notificationId: string) => void;
  themeMode?: string;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
  themeMode: _themeMode = 'light'
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Fetch notifications
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: async () => {
      const response = await api.get('/notifications?limit=50');
      return response.data.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    enabled: true
  });

  // Fetch unread count
  const { data: unreadCountData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      const response = await api.get('/notifications/unread-count');
      return response.data.data.count;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
    enabled: true
  });

  const notifications = notificationsData || [];
  const unreadCount = unreadCountData || 0;

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.put(`/notifications/${notificationId}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    }
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      await api.put('/notifications/mark-all-read');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    }
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      await api.delete(`/notifications/${notificationId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
    }
  });

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      setAnchorEl(null);
    }
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleDeleteNotification = (notificationId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    deleteNotificationMutation.mutate(notificationId);
  };

  const getNotificationIcon = (type: string, priority: string) => {
    const getColor = () => {
      if (priority === 'urgent') return 'error';
      if (priority === 'high') return 'warning';
      return 'inherit';
    };

    const iconProps = {
      fontSize: 'small' as const,
      color: getColor() as any
    };

    switch (type) {
      case 'assignment':
      case 'ticket_assigned':
        return <PersonAdd {...iconProps} />;
      case 'team_added':
        return <PersonAdd {...iconProps} />;
      case 'team_removed':
        return <Person {...iconProps} />;
      case 'comment':
        return <Comment {...iconProps} />;
      case 'reply':
        return <Reply {...iconProps} />;
      case 'mention':
        return <Reply {...iconProps} />;
      case 'status_change':
      case 'project_update':
        return <Update {...iconProps} />;
      case 'priority_change':
        return <Update {...iconProps} />;
      case 'due_date_reminder':
      case 'due_date_change':
        return <Schedule {...iconProps} />;
      case 'overdue':
        return <Schedule {...iconProps} />;
      case 'ticket_created':
      case 'ticket_reply':
        return <ConfirmationNumber {...iconProps} />;
      case 'sla_warning':
        return <ConfirmationNumber {...iconProps} />;
      case 'document_uploaded':
      case 'document_shared':
        return <Description {...iconProps} />;
      case 'purchase_order_ordered':
        return <ShoppingCart {...iconProps} />;
      case 'shipment_shipped':
      case 'shipment_delivered':
      case 'shipment_exception':
        return <LocalShipping {...iconProps} />;
      case 'milestone_completed':
        return <CheckCircle {...iconProps} />;
      case 'client_activity':
        return <Person {...iconProps} />;
      case 'approval_request':
        return <CheckCircle {...iconProps} />;
      case 'role_changed':
        return <Person {...iconProps} />;
      case 'system':
        return <Info {...iconProps} />;
      case 'birthday':
        return <Person {...iconProps} />;
      default:
        return <Info {...iconProps} />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return theme.palette.error.main;
      case 'high': return theme.palette.warning.main;
      case 'medium': return theme.palette.info.main;
      case 'low': return theme.palette.grey[400];
      default: return theme.palette.grey[400];
    }
  };

  const getNotificationDate = (notification: Notification): string => {
    const raw = notification.createdAt || notification.created_at;
    if (!raw) return 'Unknown time';
    try {
      const d = new Date(raw);
      if (isNaN(d.getTime())) return 'Unknown time';
      return dayjs(d).fromNow();
    } catch {
      return 'Unknown time';
    }
  };

  /**
   * Strip 'View details: ...' lines from notification message text.
   * The action_url is already stored separately and rendered as an 'Open' link.
   */
  const cleanMessageText = (message: string): string => {
    return message
      .replace(/\n*View details:.*$/gm, '')
      .replace(/\n{2,}$/g, '')
      .trim();
  };

  /**
   * Derive a meaningful display title for a notification.
   * When the title is the generic "Notification" fallback, extract the item
   * name from the message body (e.g. "Item: ..." line) to provide context.
   */
  const getDisplayTitle = (notification: Notification): string => {
    const title = notification.title?.trim();
    if (title && title !== 'Notification') return title;

    // Try to extract entity name from message "Item: ..." line
    const itemMatch = notification.message?.match(/Item:\s*(.+)/);
    if (itemMatch) return itemMatch[1].trim();

    // Fall back to first meaningful line of the message
    const firstLine = notification.message?.split('\n').find(l => l.trim() && !l.startsWith('View details'));
    if (firstLine) return firstLine.trim();

    return title || 'Update';
  };

  const sortedNotifications = [...notifications].sort((a, b) => {
    // Unread first, then by timestamp
    if (a.read !== b.read) {
      return a.read ? 1 : -1;
    }
    const timeA = new Date(a.createdAt || a.created_at || 0).getTime();
    const timeB = new Date(b.createdAt || b.created_at || 0).getTime();
    return timeB - timeA;
  });

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        onClick={handleMenuOpen}
        sx={{
          position: 'relative',
          overflow: 'visible',
          color: '#ffffff',
          '&:hover': {
            backgroundColor: 'rgba(255, 140, 0, 0.1)',
            // Only rattle when there's something worth pointing at.
            ...(unreadCount > 0 && {
              '& .MuiBadge-root svg': {
                animation: `${bellRattle} 0.5s ease-in-out infinite`,
                transformOrigin: 'top center',
                // SVGs default to view-box transform origin; force fill-box
                // so the "top center" pivot resolves to the icon's bbox top
                // and the bell actually swings from its mount point.
                transformBox: 'fill-box'
              }
            })
          },
          // Respect reduced-motion preference: kill the animation entirely.
          '@media (prefers-reduced-motion: reduce)': {
            '&:hover .MuiBadge-root svg': { animation: 'none' }
          }
        }}
      >
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              zIndex: 1300, // Higher than Material-UI's default z-index
              position: 'absolute',
              top: 6,
              right: 6,
              minWidth: 18,
              height: 18,
              fontSize: '0.75rem',
              fontWeight: 600
            }
          }}
        >
          <Notifications sx={{ color: '#ffffff' }} />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            maxHeight: '70vh',
            width: 400,
            maxWidth: '90vw'
          }
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight="bold">
              Notifications
              {unreadCount > 0 && (
                <Chip
                  label={unreadCount}
                  size="small"
                  color="error"
                  sx={{ ml: 1 }}
                />
              )}
            </Typography>
            <Box>
              {unreadCount > 0 && (
                <Button
                  size="small"
                  startIcon={<MarkEmailRead />}
                  onClick={handleMarkAllAsRead}
                  sx={{ mr: 1, fontSize: '0.75rem' }}
                >
                  Mark All Read
                </Button>
              )}
              <Tooltip title="Close notifications" placement="bottom" arrow>
                <IconButton size="small" onClick={handleMenuClose} aria-label="Close notifications">
                  <Close fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        {/* Content */}
        {isLoading ? (
          <Box p={3} textAlign="center">
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading...
            </Typography>
          </Box>
        ) : sortedNotifications.length === 0 ? (
          <Box p={3} textAlign="center">
            <NotificationsActive sx={{ fontSize: 32, color: 'text.secondary', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <Box sx={{ maxHeight: '50vh', overflow: 'auto' }}>
            {sortedNotifications.slice(0, 10).map((notification, index) => (
              <React.Fragment key={notification.id}>
                <MenuItem
                  onClick={() => handleNotificationClick(notification)}
                  sx={{
                    bgcolor: notification.read ? 'transparent' : alpha('#ff8c00', 0.1),
                    '&:hover': {
                      bgcolor: alpha('#ff8c00', 0.1)
                    },
                    py: 1.5,
                    px: 2
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        bgcolor: getPriorityColor(notification.priority),
                        width: 28,
                        height: 28
                      }}
                    >
                      {getNotificationIcon(notification.type, notification.priority)}
                    </Avatar>
                  </ListItemIcon>

                  <ListItemText
                    primary={
                      <Box
                        display="flex"
                        alignItems="flex-start"
                        gap={0.5}
                        flexWrap="wrap"
                        sx={{ minWidth: 0 }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: notification.read ? 'normal' : 'bold',
                            fontSize: '0.875rem',
                            flex: 1,
                            minWidth: 0,
                            wordBreak: 'break-word'
                          }}
                        >
                          {getDisplayTitle(notification)}
                        </Typography>
                        <Chip
                          label={notification.priority}
                          size="small"
                          sx={{
                            bgcolor: getPriorityColor(notification.priority),
                            color: 'white',
                            fontSize: '0.65rem',
                            height: 14,
                            flexShrink: 0,
                            '& .MuiChip-label': {
                              px: 0.5
                            }
                          }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ pr: 3, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography 
                          variant="body2" 
                          color="text.secondary" 
                          sx={{ 
                            fontSize: '0.8rem',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-line' 
                          }}
                        >
                          {cleanMessageText(notification.message)}
                        </Typography>
                        {notification.action_url && (
                          <Typography
                            variant="caption"
                            component="span"
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                              handleNotificationClick(notification);
                            }}
                            sx={{
                              fontSize: '0.75rem',
                              color: theme.palette.primary.main,
                              cursor: 'pointer',
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.3,
                              '&:hover': { textDecoration: 'underline' }
                            }}
                          >
                            Open <OpenInNew sx={{ fontSize: '0.7rem' }} />
                          </Typography>
                        )}
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                            {getNotificationDate(notification)}
                          </Typography>
                          {notification.triggeredBy && (
                            <>
                              <Person color="action" sx={{ fontSize: '0.9rem' }} />
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                {notification.triggeredBy.first_name} {notification.triggeredBy.last_name}
                              </Typography>
                            </>
                          )}
                        </Box>
                      </Box>
                    }
                  />

                  <Box display="flex" alignItems="center" gap={0.5}>
                    {!notification.read && (
                      <Tooltip title="Mark as read" placement="top" arrow>
                        <IconButton
                          size="small"
                          aria-label="Mark as read"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsReadMutation.mutate(notification.id);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <MarkEmailUnread fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Dismiss notification" placement="top" arrow>
                      <IconButton
                        size="small"
                        aria-label="Dismiss notification"
                        onClick={(e) => handleDeleteNotification(notification.id, e)}
                        sx={{ p: 0.5 }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </MenuItem>
                {index < Math.min(sortedNotifications.length, 10) - 1 && <Divider />}
              </React.Fragment>
            ))}
            {sortedNotifications.length > 10 && (
              <Box sx={{ p: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  Showing 10 of {sortedNotifications.length} notifications
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Menu>
    </>
  );
};

export default NotificationCenter;

